// Utilities for rendering a PDF page to a high-resolution canvas and
// cropping a rectangular region of it to a PNG Blob. Used by the
// ScoreExcerptPicker to turn a user-drawn rectangle on a displayed PDF
// page into an image suitable for OMR.
//
// Why a re-render at higher scale instead of grabbing the displayed canvas:
// react-pdf renders the page at a scale chosen for screen fit, which is
// often too low DPI for OMR (Audiveris expects ~200-300 DPI). Re-rendering
// at a fixed crop scale gives consistent input quality regardless of how
// the page was displayed.

import { pdfjs } from "react-pdf";

// 3x the PDF's natural 72 DPI ≈ 216 DPI — comfortable for Audiveris.
export const DEFAULT_CROP_RENDER_SCALE = 3;

/**
 * Render a single PDF page (1-indexed) to an offscreen canvas.
 *
 * @param {ArrayBuffer} pdfBuffer
 * @param {number} pageNumber 1-indexed page number.
 * @param {number} [scale]
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function renderPdfPageToCanvas(
  pdfBuffer,
  pageNumber,
  scale = DEFAULT_CROP_RENDER_SCALE
) {
  if (!pdfBuffer) throw new Error("renderPdfPageToCanvas: missing pdfBuffer");
  // pdfjs.getDocument consumes (or transfers) the buffer in some configs;
  // pass a copy so the caller can re-use the original.
  const data = pdfBuffer.slice(0);
  const pdf = await pdfjs.getDocument({ data }).promise;
  try {
    if (pageNumber < 1 || pageNumber > pdf.numPages) {
      throw new Error(
        `Page ${pageNumber} out of range (PDF has ${pdf.numPages} pages)`
      );
    }
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport }).promise;
    return canvas;
  } finally {
    pdf.destroy();
  }
}

/**
 * Crop a rectangular region of a source canvas to a PNG Blob.
 * Coordinates are in source-canvas pixels.
 *
 * @param {HTMLCanvasElement} sourceCanvas
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @returns {Promise<Blob>}
 */
export function cropCanvasToPngBlob(sourceCanvas, x, y, width, height) {
  if (width <= 0 || height <= 0) {
    return Promise.reject(new Error("Crop region must be positive in size."));
  }
  const clamped = clampToCanvas(sourceCanvas, x, y, width, height);
  const cropCanvas = document.createElement("canvas");
  cropCanvas.width = clamped.width;
  cropCanvas.height = clamped.height;
  const ctx = cropCanvas.getContext("2d");
  ctx.drawImage(
    sourceCanvas,
    clamped.x,
    clamped.y,
    clamped.width,
    clamped.height,
    0,
    0,
    clamped.width,
    clamped.height
  );
  return new Promise((resolve, reject) => {
    cropCanvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to encode crop as PNG."));
      },
      "image/png"
    );
  });
}

function clampToCanvas(canvas, x, y, w, h) {
  const cx = Math.max(0, Math.floor(x));
  const cy = Math.max(0, Math.floor(y));
  const cw = Math.min(canvas.width - cx, Math.ceil(w));
  const ch = Math.min(canvas.height - cy, Math.ceil(h));
  return { x: cx, y: cy, width: cw, height: ch };
}
